'use client'

import { useState } from 'react'

const ENQUIRY_TYPES = [
  { value: 'contact_form',    label: 'General enquiry' },
  { value: 'support_request', label: 'Technical support' },
  { value: 'sales_enquiry',   label: 'Sales / pricing' },
  { value: 'bug_report',      label: 'Bug report' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'sales_enquiry_partnership', label: 'Partnership / wholesale' },
] as const

type ContactFormState = {
  enquiry_type: string
  name: string
  email: string
  phone: string
  company: string
  subject: string
  message: string
  honeypot: string
}

const INITIAL_STATE: ContactFormState = {
  enquiry_type: 'contact_form',
  name: '',
  email: '',
  phone: '',
  company: '',
  subject: '',
  message: '',
  honeypot: '',
}

export function ContactForm() {
  const [form, setForm] = useState<ContactFormState>(INITIAL_STATE)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  function setField<K extends keyof ContactFormState>(key: K, value: ContactFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  async function submit() {
    setSubmitting(true)
    setError(null)
    setSuccess(null)

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })

      const payload = await response.json().catch(() => ({})) as { error?: string }
      if (!response.ok) {
        setError(payload.error ?? 'Could not submit right now. Please retry in a moment.')
        return
      }

      setSuccess('Thanks, your message has been sent. Our team will get back to you shortly.')
      setForm(INITIAL_STATE)
    } catch {
      setError('Could not submit right now. Please retry in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <section className="bb-card rounded-[24px] p-5 sm:p-7">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#c2571a]">Contact BuilderBuddi</p>
      <h2 className="mt-2 text-2xl font-semibold text-slate-900">Tell us what you need</h2>
      <p className="mt-1 text-sm text-slate-600">We route this directly to operations so nothing gets missed.</p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-slate-700 sm:col-span-2">
          Enquiry type
          <select className="bb-field mt-1 w-full" value={form.enquiry_type} onChange={(event) => setField('enquiry_type', event.target.value)}>
            {ENQUIRY_TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="text-sm text-slate-700">
          Name
          <input className="bb-field mt-1 w-full" value={form.name} onChange={(event) => setField('name', event.target.value)} />
        </label>
        <label className="text-sm text-slate-700">
          Email
          <input className="bb-field mt-1 w-full" type="email" value={form.email} onChange={(event) => setField('email', event.target.value)} />
        </label>
        <label className="text-sm text-slate-700">
          Phone (optional)
          <input className="bb-field mt-1 w-full" value={form.phone} onChange={(event) => setField('phone', event.target.value)} />
        </label>
        <label className="text-sm text-slate-700">
          Company (optional)
          <input className="bb-field mt-1 w-full" value={form.company} onChange={(event) => setField('company', event.target.value)} />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Subject
          <input className="bb-field mt-1 w-full" value={form.subject} onChange={(event) => setField('subject', event.target.value)} />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Message
          <textarea className="bb-field mt-1 min-h-[160px] w-full" value={form.message} onChange={(event) => setField('message', event.target.value)} />
        </label>

        <label className="hidden" aria-hidden>
          Website
          <input tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={(event) => setField('honeypot', event.target.value)} />
        </label>
      </div>

      {error && <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
      {success && <p className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">{success}</p>}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={submitting}
        className="bb-btn-primary mt-5 w-full sm:w-auto"
      >
        {submitting ? 'Sending...' : 'Send message'}
      </button>
    </section>
  )
}
